import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roundRobinApi } from '@/lib/api/services/roundRobin';
import {
  Users,
  UserPlus,
  Search,
  Edit,
  Trash2,
  Mail,
  Loader2,
  Clock,
  CheckCircle,
  AlertCircle,
  Key,
  MessageCircle,
  Hash,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  Lock,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChatWindow } from '@/components/chat/ChatWindow';
import { DirectMessageWindow } from '@/components/chat/DirectMessageWindow';
import { TeamMembersList } from '@/components/chat/TeamMembersList';
import { UserSearchModal } from '@/components/chat/UserSearchModal';
import { Skeleton } from '@/components/ui/skeleton';
import { RoleBadge } from './RoleBadge';
import { MinimalInviteDialog } from '@/components/teams/MinimalInviteDialog';
import { MinimalEditDialog } from '@/components/teams/MinimalEditDialog';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { MemberPermissionsDialog } from './MemberPermissionsDialog';
import { TeamService, TeamMember, TeamStats } from '@/lib/api/services/teamService';
import { useToast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors/showApiError';
import { useAuth } from '@/components/AuthProvider';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { Building2, Phone } from 'lucide-react';
import { useChat } from '@/contexts/ChatContext';

const TeamMemberSkeleton = () => (
  <Card className="h-full rounded-[12px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)] border-0">
    <CardContent className="p-4 flex flex-col h-full">
      <div className="flex items-start gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="min-w-0 flex-1">
          <div className="flex justify-between items-center gap-2 mb-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="mt-3">
        <Skeleton className="h-3 w-24 mb-1" />
        <div className="flex gap-1">
          <Skeleton className="h-6 w-20 rounded-md" />
          <Skeleton className="h-6 w-20 rounded-md" />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <Skeleton className="h-6 w-16 rounded-full" />
        <div className="flex gap-1">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const MinimalTeamManagement: React.FC = () => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get('search') || '';
  const [searchTerm, setSearchTerm] = useState(searchParam);

  // Sync state with URL param on mount/update
  useEffect(() => {
    setSearchTerm(searchParam);
  }, [searchParam]);

  // Debounce search update to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== searchParam) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          if (searchTerm) next.set('search', searchTerm);
          else next.delete('search');
          return next;
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchParam, setSearchParams]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<TeamMember | null>(null);

  const [passwordChangeUser, setPasswordChangeUser] = useState<TeamMember | null>(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState<TeamMember | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: businessAccounts } = useBusinessAccounts();
  const { data: entitlements } = useOrganizationEntitlements();
  const chatContext = useChat();
  const orgTeamRoomId = currentUser?.organization_id ? `org_${currentUser.organization_id}_team_general` : 'team-general';

  // Sync active tab with URL param so refresh preserves the selected tab
  const validTabs = ['members', 'messaging', 'round-robin'];
  const tabParam = searchParams.get('tab') || 'members';
  const activeManagementTab = validTabs.includes(tabParam) ? tabParam : 'members';
  const setActiveManagementTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };
  const [messagingTab, setMessagingTab] = useState<'team' | 'chat'>('team');
  const [messagingSelectedMember, setMessagingSelectedMember] = useState<string | null>(null);

  const parseAccessFlag = (value: unknown): boolean => {
    return value === true || value === 1 || value === '1' || value === 'true';
  };
  const [messagingSelectedMemberName, setMessagingSelectedMemberName] = useState<string>('');
  const [isUserSearchOpen, setIsUserSearchOpen] = useState(false);

  const handleMessagingMemberSelect = (memberId: string) => {
    const member = chatContext?.teamMembers?.find(m => m.id === memberId);
    setMessagingSelectedMember(memberId);
    setMessagingSelectedMemberName(member?.full_name || member?.email || 'Unknown');
    setMessagingTab('chat');
  };

  const handleMessagingStartDM = (userId: string, userName: string) => {
    setMessagingSelectedMember(userId);
    setMessagingSelectedMemberName(userName);
    setMessagingTab('chat');
    setIsUserSearchOpen(false);
  };

  // Ensure chat socket is connected so we receive presence events
  useEffect(() => {
    if (!chatContext) return;
    if (!chatContext.isConnected && !chatContext.isConnecting) {
      chatContext.connect();
    }
  }, [chatContext]);

  // Listen for live presence updates via socket
  useEffect(() => {
    if (!chatContext?.socket) return;

    const handleUserStatus = (data: { userId: string; is_online: boolean; last_seen: string }) => {
      setTeamMembers((prev) =>
        prev.map((member) =>
          member.id === data.userId
            ? { ...member, is_online: data.is_online, last_seen: data.last_seen }
            : member
        )
      );
    };

    chatContext.socket.on('user_status', handleUserStatus);

    return () => {
      chatContext.socket?.off('user_status', handleUserStatus);
    };
  }, [chatContext?.socket]);

  const presenceMap = useMemo(() => {
    if (!chatContext?.teamMembers || chatContext.teamMembers.length === 0) return null;

    return chatContext.teamMembers.reduce<Record<string, { is_online?: boolean; last_seen?: string }>>((acc, member) => {
      acc[member.id] = {
        is_online: member.is_online,
        last_seen: member.last_seen,
      };
      return acc;
    }, {});
  }, [chatContext?.teamMembers]);

  const membersWithPresence = useMemo(() => {
    if (!presenceMap) return teamMembers;

    return teamMembers.map((member) => {
      const presence = presenceMap[member.id];
      if (!presence) return member;

      return {
        ...member,
        is_online: presence.is_online ?? member.is_online,
        last_seen: presence.last_seen ?? member.last_seen,
      };
    });
  }, [teamMembers, presenceMap]);

  // Load team members and stats
  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [membersResponse, statsResponse] = await Promise.all([
        TeamService.getTeamMembers(),
        TeamService.getTeamStats()
      ]);


      if ((membersResponse as any)?.success) {
        setTeamMembers(((membersResponse as any).data) as any);
      } else {
      }

      if ((statsResponse as any)?.success) {
        setTeamStats(((statsResponse as any).data) as any);
      } else {
      }
    } catch (err) {
      setError('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const roleRaw = (currentUser?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(currentUser as any)?.is_super_admin;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin;

  // Show EVERY seat, including the owner and yourself — so the list count
  // matches the "X of Y seats used" header and the plan limit the backend
  // enforces (which counts all profiles). The owner + your own row are
  // rendered read-only below (see lockActions), the standard Pattern-1 SaaS
  // behaviour (Slack/Linear/Notion): you can see the owner's seat but can't
  // edit, deactivate, or delete the account owner.
  const filteredMembers = membersWithPresence.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Honest seat usage for the header. The plan's `users` limit counts ACTIVE
  // members only (a deactivated member frees their seat — standard per-seat
  // SaaS), and the owner/you always occupy a seat. The header shows this same
  // active figure so the number matches what the backend enforces on invite.
  //   active_members from team stats EXCLUDES the requesting user; you're
  //   logged in and therefore active, so total active seats = active_members + 1.
  //   Pagination-safe (stats query uses limit:1000) unlike teamMembers.length.
  // Prefer the seat cap the stats endpoint resolved via the backend's own
  // getLimit (authoritative, matches enforcement); fall back to the entitlements
  // payload only if stats didn't carry it.
  const seatLimit = teamStats?.seat_limit ?? entitlements?.entitlements?.limits?.users;
  const seatsUsed = teamStats
    ? teamStats.active_members + 1
    : membersWithPresence.filter((m) => m.is_active).length;
  const seatUsageLabel =
    typeof seatLimit === 'number' && seatLimit > 0
      ? `${seatsUsed} of ${seatLimit} members active`
      : `${seatsUsed} member${seatsUsed === 1 ? '' : 's'} active`;
  // Only a finite, positive cap can be "reached" (unlimited plans have seatLimit
  // -1/undefined and never disable the button). Mirrors the backend gate, which
  // blocks the invite when active members >= the plan's users limit.
  const seatLimitReached =
    typeof seatLimit === 'number' && seatLimit > 0 && seatsUsed >= seatLimit;

  // Per-role usage summary ("Admins 1/10 · Users 5/10 · Agents 3/10").
  // Resolved server-side via the authoritative path (plan + manual override),
  // so this reflects exactly what the invite gate enforces per role. Only
  // shown when the plan defines per-role caps (role_limits present).
  const roleLimits = teamStats?.role_limits;
  const roleUsage = teamStats?.role_usage;
  const fmtCap = (cap: number | null | undefined) =>
    cap === -1 ? '∞' : typeof cap === 'number' ? String(cap) : '—';
  const perRoleSummary =
    roleLimits && roleUsage &&
    [roleLimits.admin, roleLimits.user, roleLimits.agent].some((c) => typeof c === 'number')
      ? `Admins ${roleUsage.admin}/${fmtCap(roleLimits.admin)} · Users ${roleUsage.user}/${fmtCap(roleLimits.user)} · Agents ${roleUsage.agent}/${fmtCap(roleLimits.agent)}`
      : null;

  // Helper function to get assigned business accounts for a member
  const getAssignedBusinessAccounts = (member: TeamMember) => {
    // Use the assigned_accounts from the API response
    if (member.assigned_accounts && member.assigned_accounts.length > 0) {
      return member.assigned_accounts;
    }

    // Fallback for owners/admins if no specific assignments are returned (legacy behavior compatibility)
    // Though ideally backend returns everything.
    if (member.is_organization_owner || member.is_super_admin || (member.role || '').toString().toUpperCase() === 'ADMIN') {
      // Map to match the structure if pulling from context
      // We don't have permissions info here, so default to empty
      return (businessAccounts || []).map(ba => ({
        id: ba.id,
        name: ba.name,
        permissions: { has_all_conversations_access: true } // Owners usually have full access
      }));
    }

    return [];
  };

  const getPresenceDotClass = (isOnline?: boolean, status?: string) => {
    if (status === 'inactive') {
      return 'bg-red-500';
    }
    return isOnline ? 'bg-green-500' : 'bg-gray-300';
  };

  const getPresenceText = (isOnline?: boolean, status?: string) => {
    if (status === 'inactive') {
      return 'Inactive';
    }
    return isOnline ? 'Online' : 'Offline';
  };

  // Helper function to determine invitation status
  const getInvitationStatus = (member: TeamMember) => {
    const now = new Date();
    const createdAt = new Date(member.created_at);
    const lastLogin = member.last_login ? new Date(member.last_login) : (member as any).last_login_at ? new Date((member as any).last_login_at) : null;

    // Check if created in last 24 hours
    const isRecentlyInvited = (now.getTime() - createdAt.getTime()) < 24 * 60 * 60 * 1000;

    // Check if never logged in
    const neverLoggedIn = !lastLogin;

    if (neverLoggedIn && isRecentlyInvited) {
      return { status: 'pending', label: 'Invitation Pending', icon: Clock, color: 'text-orange-600' };
    } else if (neverLoggedIn) {
      return { status: 'never-logged-in', label: 'Never Logged In', icon: AlertCircle, color: 'text-red-600' };
    } else {
      return null; // No badge for active users
    }
  };

  const handleEditUser = (user: TeamMember) => {
    setEditingUser(user);
  };



  const handleInviteMember = async (userData: any) => {
    try {
      const response = await TeamService.inviteMember(userData);

      if (response.success) {
        await loadTeamData(); // Reload data
        setIsInviteDialogOpen(false);
        const password = (response.data as any)?.password;
        toast({
          title: "Success",
          description: password
            ? `Team member created successfully! Password: ${password}`
            : "Team member created successfully!",
          variant: "success",
          duration: password ? 10000 : 5000, // Show longer if password is displayed
        });
      } else {
        showApiError(response, { fallback: { title: "Couldn't invite team member", description: "Failed to invite team member." } });
      }
    } catch (err: any) {
      showApiError(err, { fallback: { title: "Couldn't invite team member", description: "Failed to invite team member." } });
    }
  };

  const handleUpdateMember = async (updatedUser: any) => {
    try {
      // Capture the role BEFORE the update so we can detect a real role change.
      // editingUser holds the member being edited (its original role).
      const previousRole = (editingUser?.role || '').toString().trim().toUpperCase();
      const newRole = (updatedUser?.role || '').toString().trim().toUpperCase();
      const editedMember = editingUser;

      const payload = {
        ...updatedUser,
        hasAllConversationsAccess:
          updatedUser.hasAllConversationsAccess ?? updatedUser.has_all_conversations_access,
      };
      const response = await TeamService.updateMember(updatedUser.id, payload);
      if (response.success) {
        await loadTeamData(); // Reload data
        setEditingUser(null);

        // Classify the change so the toast (and the permissions picker) match
        // what actually happened.
        const roleChanged = !!previousRole && !!newRole && previousRole !== newRole;
        const roleChangedToNonAdmin = roleChanged && newRole !== 'ADMIN';
        const roleChangedToAdmin = roleChanged && newRole === 'ADMIN';

        if (roleChangedToNonAdmin) {
          // Contextual toast — explains WHY the permissions picker is opening, so
          // the popping dialog doesn't feel abrupt.
          const roleLabel = newRole === 'AGENT' ? 'Agent' : newRole === 'USER' ? 'User' : newRole;
          toast({
            title: `Role changed to ${roleLabel}`,
            description: `Choose which sidebar sections this ${roleLabel.toLowerCase()} can access.`,
            variant: "success",
          });
        } else if (roleChangedToAdmin) {
          toast({
            title: "Team member updated",
            description: "Role changed to Admin — full access granted.",
            variant: "success",
          });
        } else {
          toast({
            title: "Success",
            description: "Team member updated successfully",
            variant: "success",
          });
        }

        // When a member is changed to a NON-ADMIN role (agent/user), the backend
        // has reset their menu_permissions to that role's default. Open the
        // EXISTING Menu Permissions dialog so the admin can assign the specific
        // sidebar sections for the new role — instead of it silently defaulting.
        // Promotion to ADMIN needs no picker (admins get full access already).
        // We pass menu_permissions:null / preferences:null on the snapshot so it
        // matches the just-reset DB value and the dialog's save-time conflict
        // check doesn't false-trigger.
        if (roleChangedToNonAdmin) {
          setPermissionsUser({
            ...(editedMember || {}),
            ...updatedUser,
            role: newRole,
            menu_permissions: null,
            preferences: null,
          } as any);
        }
      } else {
        showApiError(response, { fallback: { title: "Couldn't update team member", description: "Failed to update team member." } });
      }
    } catch (err: any) {
      showApiError(err, { fallback: { title: "Couldn't update team member", description: "Failed to update team member." } });
    }
  };

  const handleChangePassword = async (userId: string, newPassword: string) => {
    try {
      const response = await TeamService.changeUserPassword(userId, newPassword);
      if (response.success) {
        toast({
          title: "Success",
          description: "Password changed successfully",
          variant: "success",
        });
      } else {
        showApiError(response, { fallback: { title: "Couldn't change password", description: "Failed to change password." } });
      }
    } catch (err) {
      showApiError(err, { fallback: { title: "Couldn't change password", description: "Failed to change password." } });
      throw err; // Re-throw to handle in dialog
    }
  };

  const handlePasswordChangeClick = (user: TeamMember) => {
    setPasswordChangeUser(user);
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordChangeConfirm = async (newPassword: string) => {
    if (!passwordChangeUser) return;

    try {
      setPasswordLoading(true);
      await handleChangePassword(passwordChangeUser.id, newPassword);
      setIsPasswordDialogOpen(false);
      setPasswordChangeUser(null);
    } catch (error) {
    } finally {
      setPasswordLoading(false);
    }
  };

  // Robustly extract a member's stored menu_permissions regardless of whether
  // `preferences` came back as a parsed object or a JSON string (Drizzle/RDS
  // Data API can return JSON columns either way). Returning a normalized value
  // here prevents a shape mismatch from triggering a false "conflict".
  const extractMenuPerms = (m: any): string[] | null => {
    if (!m) return null;
    let prefs = m.preferences;
    if (typeof prefs === 'string') {
      try { prefs = JSON.parse(prefs); } catch { prefs = null; }
    }
    const mp = prefs?.menu_permissions ?? m.menu_permissions ?? null;
    return Array.isArray(mp) ? mp : null;
  };

  const handleSavePermissions = async (memberId: string, menuPermissions: string[] | null) => {
    try {
      // Fetch latest member data to detect conflicts
      const memberResponse = await TeamService.getTeamMembers();
      if (memberResponse.success && Array.isArray(memberResponse.data)) {
        const latestMember = memberResponse.data.find(m => m.id === memberId);
        if (latestMember) {
          const latestPerms = extractMenuPerms(latestMember);
          const currentPerms = extractMenuPerms(permissionsUser);

          // Detect if permissions were changed elsewhere. Compare order-independently
          // so a reordered-but-equivalent array isn't flagged as a conflict.
          const norm = (v: string[] | null) => (v === null ? null : [...v].sort());
          if (JSON.stringify(norm(latestPerms)) !== JSON.stringify(norm(currentPerms))) {
            toast({
              title: 'Conflict Detected',
              description: 'Permissions were changed elsewhere. Reloading...',
              variant: 'destructive',
            });
            await loadTeamData();
            return;
          }
        }
      }

      // Save the new permissions
      const response = await TeamService.updateMember(memberId, { menuPermissions });
      if (response.success) {
        await loadTeamData();
        toast({ variant: "success", title: 'Success', description: 'Permissions updated successfully' });
      } else {
        showApiError(response, { fallback: { title: "Couldn't update permissions", description: 'Failed to update permissions.' } });
      }
    } catch (error: any) {
      console.error('Error saving permissions:', error);
      showApiError(error, { fallback: { title: "Couldn't update permissions", description: 'Failed to save permissions.' } });
    }
  };

  const handlePasswordDialogClose = (open: boolean) => {
    setIsPasswordDialogOpen(open);
    if (!open) {
      // Clear state when dialog is closed
      setPasswordChangeUser(null);
      setPasswordLoading(false);
    }
  };

  return (
    <div className="p-0 sm:p-0 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Team Management</h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
            {activeManagementTab === 'members' ? seatUsageLabel : 'Chat with your team'}
          </p>
          {activeManagementTab === 'members' && perRoleSummary && (
            <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
              {perRoleSummary}
            </p>
          )}
        </div>
        {activeManagementTab === 'members' && isAdmin && (
          seatLimitReached ? (
            // Reason is shown ALWAYS (not only on hover) directly under the
            // button, so an admin immediately sees why they can't add a member.
            <div className="flex flex-col items-start sm:items-end gap-1.5">
              <Button size="sm" disabled className="w-fit">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Member
              </Button>
              <p className="text-xs text-amber-600 dark:text-amber-500 leading-relaxed text-left sm:text-right sm:max-w-xs">
                You've reached your plan limit ({seatLimit} members).<br />
                Deactivate a member or upgrade the plan to add more.
              </p>
            </div>
          ) : (
            <Button size="sm" onClick={() => setIsInviteDialogOpen(true)} className="w-fit">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Member
            </Button>
          )
        )}
      </div>

      {/* Tabs: Members / Messaging / Round Robin */}
      <Tabs value={activeManagementTab} onValueChange={setActiveManagementTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="members" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Members
          </TabsTrigger>
          <TabsTrigger value="messaging" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Messaging
          </TabsTrigger>
          <TabsTrigger value="round-robin" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Round Robin
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4 space-y-4">
          {loading ? (
            <>
              <div className="relative w-full sm:max-w-md">
                <Skeleton className="h-9 w-full rounded-[12px]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <TeamMemberSkeleton key={i} />)}
              </div>
            </>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <Button size="sm" onClick={loadTeamData}>Retry</Button>
            </div>
          ) : (!businessAccounts || businessAccounts.length === 0) ? (
            <Card>
              <CardContent className="p-6 text-center">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Business Accounts</h3>
                <p className="text-muted-foreground mb-4">
                  You need to add a business account before you can add team members.
                </p>
                <Button size="sm" onClick={() => navigate('/home/business-accounts')}>
                  Go to Business Accounts
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="relative w-full sm:max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-[12px] bg-[#F5F7F9] text-xs sm:text-sm h-8 sm:h-9"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMembers.map((member) => {
                  const invitationStatus = getInvitationStatus(member);
                  const StatusIcon = invitationStatus?.icon;
                  const isSelf = !!(currentUser && member.email === currentUser.email);
                  const isOwnerRow = !!member.is_organization_owner;
                  // The owner can never be edited/removed (would orphan the org),
                  // and your own row stays read-only in this list too. Both render
                  // a lock chip instead of the edit/permissions/password actions.
                  const lockActions = isOwnerRow || isSelf;

                  return (
                    <Card key={member.id} className="h-full rounded-[12px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)] border-0  hover:shadow-md transition-shadow">
                      <CardContent className="p-4 flex flex-col h-full">
                        <div className="flex items-start gap-3">
                          <div className="relative">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback>
                                {member.name.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div
                              className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-background ${getPresenceDotClass(member.is_online, member.status)}`}
                              aria-label={getPresenceText(member.is_online, member.status)}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap justify-between items-center gap-2">
                              <h4 className="font-medium truncate">{member.name}</h4>
                              <Badge
                                variant={member.is_active ? 'default' : 'secondary'}
                                className={`text-xs ${member.is_active
                                  ? 'bg-green-500 text-white hover:bg-green-600'
                                  : 'bg-red-500 text-white hover:bg-red-600'
                                  }`}
                              >
                                {member.is_active ? 'active' : 'inactive'}
                              </Badge>
                              {isOwnerRow && (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-amber-50 text-amber-700 border-amber-300"
                                >
                                  Owner
                                </Badge>
                              )}
                              {isSelf && !isOwnerRow && (
                                <Badge variant="outline" className="text-xs">
                                  You
                                </Badge>
                              )}
                              {invitationStatus && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${invitationStatus.status === 'pending'
                                    ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600'
                                    : 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                                    }`}
                                >
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {invitationStatus.label}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground break-all">
                              <Mail className="h-3 w-3" />
                              {member.email}
                            </div>
                          </div>
                        </div>

                        {businessAccounts && businessAccounts.length > 0 && getAssignedBusinessAccounts(member).length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs text-muted-foreground mb-1">Business Accounts:</div>
                            <div className="flex flex-wrap gap-1">
                              {getAssignedBusinessAccounts(member).slice(0, 3).map((ba) => {
                                const hasAllAccess = parseAccessFlag(ba.permissions?.has_all_conversations_access);
                                return (
                                  <div
                                    key={ba.id}
                                    className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-md text-xs border"
                                    title={hasAllAccess ? "Full Conversation Access" : "Restricted Access"}
                                  >
                                    <Building2 className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium">{ba.name}</span>
                                    {hasAllAccess ? (
                                      <span className="text-amber-500 text-[10px] ml-1">★</span>
                                    ) : (
                                      <span className="text-muted-foreground text-[10px] ml-1">🔐</span>
                                    )}
                                  </div>
                                )
                              })}
                              {getAssignedBusinessAccounts(member).length > 3 && (
                                <div className="px-2 py-1 bg-muted/50 rounded-md text-xs text-muted-foreground border">
                                  +{getAssignedBusinessAccounts(member).length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <RoleBadge role={member.role as any} size="sm" />
                          </div>
                          <div className="flex gap-1">
                            {lockActions ? (
                              <span
                                className="flex items-center gap-1 text-xs text-muted-foreground"
                                title={isOwnerRow
                                  ? "The organization owner can't be edited, deactivated, or removed."
                                  : "This is your own account."}
                              >
                                <Lock className="h-3 w-3" />
                                {isOwnerRow ? 'Read-only' : 'You'}
                              </span>
                            ) : (
                              <>
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditUser(member)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setPermissionsUser(member)}
                                    className="h-8 w-8 p-0"
                                    title="Configure menu permissions"
                                  >
                                    <ShieldCheck className="h-4 w-4" />
                                  </Button>
                                )}
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePasswordChangeClick(member)}
                                    className="h-8 w-8 p-0"
                                    title="Change Password"
                                  >
                                    <Key className="h-4 w-4" />
                                  </Button>
                                )}
                                {!isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toast({ title: "Permission Denied", description: "Only admins can deactivate team members. Please contact your admin.", variant: "warning" });
                                    }}
                                  >
                                    <ShieldAlert className="h-3 w-3 mr-1" />
                                    Contact Admin
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              {filteredMembers.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No members found</h3>
                    <p className="text-muted-foreground mb-4">
                      {searchTerm ? 'Try adjusting your search' : 'Get started by adding your first team member'}
                    </p>
                    {!searchTerm && isAdmin && (
                      <Button size="sm" onClick={() => setIsInviteDialogOpen(true)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add First Member
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Messaging Tab */}
        <TabsContent value="messaging" className="mt-0">
          <div style={{ height: 'calc(100vh - 220px)' }} className="flex min-h-0 border rounded-lg overflow-hidden">
            {/* Sidebar — team members list with badges */}
            <div className="w-72 border-r flex-shrink-0">
              <TeamMembersList
                onSelectMember={handleMessagingMemberSelect}
                onSearchUser={() => setIsUserSearchOpen(true)}
              />
            </div>

            {/* Main chat area */}
            <div className="flex-1 flex flex-col min-h-0">
              {/* Inner tab bar: Team Chat | Direct Message */}
              <div className="flex border-b flex-shrink-0">
                <button
                  onClick={() => setMessagingTab('team')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${messagingTab === 'team'
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                  <Hash className="h-4 w-4" />
                  Team Chat
                </button>
                <button
                  onClick={() => setMessagingTab('chat')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${messagingTab === 'chat'
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                  <MessageCircle className="h-4 w-4" />
                  Direct Message
                </button>
              </div>

              {/* Chat content panel */}
              <div className="flex-1 min-h-0 overflow-hidden">
                {messagingTab === 'team' ? (
                  <ChatWindow roomId={orgTeamRoomId} roomName="General Team Chat" className="h-full rounded-none border-0" />
                ) : messagingSelectedMember ? (
                  <DirectMessageWindow
                    recipientId={messagingSelectedMember}
                    recipientName={messagingSelectedMemberName}
                    onClose={() => { setMessagingSelectedMember(null); setMessagingSelectedMemberName(''); }}
                    className="h-full rounded-none border-0"
                  />
                ) : (
                  <div className="h-full flex items-center justify-center bg-muted/20">
                    <div className="text-center max-w-md px-6">
                      <div className="w-20 h-20 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-6">
                        <MessageCircle className="h-10 w-10 text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">Start a Direct Message</h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        Choose a team member from the sidebar or search for a user to start a private conversation
                      </p>
                      <Button onClick={() => setIsUserSearchOpen(true)} className="flex items-center gap-2 px-6">
                        <Search className="h-4 w-4" />
                        Find User
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <UserSearchModal
            isOpen={isUserSearchOpen}
            onClose={() => setIsUserSearchOpen(false)}
            onStartDirectMessage={handleMessagingStartDM}
          />
        </TabsContent >

        <TabsContent value="round-robin" className="mt-4 space-y-6">
          <RoundRobinTab toast={toast} />
        </TabsContent>
      </Tabs >

      {/* Dialogs */}
      < MinimalInviteDialog
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onInvite={handleInviteMember}
      />

      {editingUser && (
        <MinimalEditDialog
          open={!!editingUser}
          onOpenChange={() => setEditingUser(null)}
          user={editingUser}
          onSave={handleUpdateMember}
        />
      )}



      <ChangePasswordDialog
        open={isPasswordDialogOpen}
        onOpenChange={handlePasswordDialogClose}
        onConfirm={handlePasswordChangeConfirm}
        memberName={passwordChangeUser?.name || ''}
        memberEmail={passwordChangeUser?.email || ''}
        loading={passwordLoading}
      />

      {permissionsUser && (
        <MemberPermissionsDialog
          open={!!permissionsUser}
          onOpenChange={(open) => { if (!open) setPermissionsUser(null); }}
          member={permissionsUser}
          onSave={handleSavePermissions}
        />
      )}
    </div >
  );
};

// ─── Round Robin Tab ──────────────────────────────────────────────────────────

interface RoundRobinTabProps {
  toast: ReturnType<typeof useToast>['toast'];
}

const RoundRobinTab: React.FC<RoundRobinTabProps> = ({ toast }) => {
  const queryClient = useQueryClient();

  const { data: settingsRes, isLoading: settingsLoading } = useQuery({
    queryKey: ['round-robin-settings'],
    queryFn: () => roundRobinApi.getSettings(),
  });

  const { data: agentsRes, isLoading: agentsLoading } = useQuery({
    queryKey: ['round-robin-agents'],
    queryFn: () => roundRobinApi.getAgents(),
  });

  const settings = settingsRes?.data;
  const agents = agentsRes?.data ?? [];

  const updateSettingsMutation = useMutation({
    mutationFn: (is_enabled: boolean) => roundRobinApi.updateSettings({ is_enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round-robin-settings'] });
      toast({ variant: "success", title: 'Round robin setting saved' });
    },
    onError: (error) => showApiError(error, { fallback: { title: 'Failed to update setting' } }),
  });

  const updateAgentMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      roundRobinApi.updateAgent(userId, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['round-robin-agents'] }),
    onError: (error) => showApiError(error, { fallback: { title: 'Failed to update agent' } }),
  });

  return (
    <div className="space-y-6">
      {/* Org-level toggle */}
      <Card className="rounded-[12px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)] border-0">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-sm">Auto-Assign (Round Robin)</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Automatically assign new WhatsApp conversations to agents in rotation
              </p>
            </div>
            {settingsLoading ? (
              <Skeleton className="h-6 w-11 rounded-full" />
            ) : (
              <Switch
                checked={settings?.is_enabled ?? false}
                onCheckedChange={(checked) => updateSettingsMutation.mutate(checked)}
                disabled={updateSettingsMutation.isPending}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Agent list — only shown when enabled */}
      {settings?.is_enabled && (
        <Card className="rounded-[12px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)] border-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Agents in Rotation</CardTitle>
            <CardDescription className="text-xs">
              Toggle which agents participate in round-robin assignment
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {agentsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-6 w-11 rounded-full ml-auto" />
                </div>
              ))
            ) : agents.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No agents found</p>
            ) : (
              agents.map((agent) => {
                // is_active is only ever explicitly false when the backend sends
                // it; a sparse/legacy payload without the field defaults to active
                // so we never falsely disable someone's toggle.
                const isInactive = agent.is_active === false;
                const switchEl = (
                  <Switch
                    checked={agent.round_robin_enabled ?? false}
                    onCheckedChange={(checked) =>
                      updateAgentMutation.mutate({ userId: agent.user_id, enabled: checked })
                    }
                    disabled={updateAgentMutation.isPending || isInactive}
                  />
                );
                return (
                  <div key={agent.user_id || agent.id} className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="text-xs">
                        {(agent.name || 'A').charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{agent.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${agent.is_online ? 'bg-green-500' : 'bg-gray-300'}`}
                        />
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                          {agent.role}
                        </Badge>
                        {isInactive && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-gray-100 text-gray-500">
                            inactive
                          </Badge>
                        )}
                      </div>
                    </div>
                    {isInactive ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* Disabled elements don't reliably fire hover events in
                              every browser, so the tooltip trigger wraps a plain
                              span rather than relying on the Switch itself. */}
                          <span className="inline-flex" tabIndex={0}>
                            {switchEl}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          This member is inactive — reactivate them to enable round-robin.
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      switchEl
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
